import React, { useEffect, useRef, useState } from "react";

const ACCENT = "rgb(45, 70, 105)";

/* ---------- Kleine Zeichnungen für die Beispiele ---------- */

const Scene = ({ bg = "#e9eef4", table = "#d8c3a5", dim = 0, blur = false, children }) => (
  <svg
    viewBox="0 0 240 160"
    width="100%"
    aria-hidden="true"
    focusable="false"
    style={{ display: "block", boxSizing: "border-box", borderRadius: 8, border: "1px solid #e5e8ec", background: bg }}
  >
    <defs>
      <filter id="rvt-blur" x="-10%" y="-10%" width="120%" height="120%">
        <feGaussianBlur stdDeviation="3.5" />
      </filter>
    </defs>
    <rect width="240" height="160" fill={bg} />
    <g filter={blur ? "url(#rvt-blur)" : undefined}>
      {table && <rect y="118" width="240" height="42" fill={table} />}
      {children}
    </g>
    {dim > 0 && <rect width="240" height="160" fill="#000" opacity={dim} />}
  </svg>
);

const Mug = ({ x = 0, y = 0, scale = 1 }) => (
  <g transform={`translate(${x} ${y}) scale(${scale})`}>
    <ellipse cx="115" cy="118" rx="40" ry="6" fill="#000" opacity="0.15" />
    <path d="M143 62c22 0 22 38 0 38" fill="none" stroke="#2d4669" strokeWidth="9" strokeLinecap="round" />
    <rect x="85" y="45" width="60" height="72" rx="9" fill="#2d4669" />
    <rect x="85" y="45" width="60" height="10" rx="5" fill="#3c5a86" />
    <rect x="95" y="72" width="40" height="5" rx="2.5" fill="#f2c94c" />
    <rect x="95" y="84" width="28" height="5" rx="2.5" fill="#f2c94c" />
  </g>
);

const Person = () => (
  <g>
    <path d="M8 118c0-26 14-34 32-34s32 8 32 34z" fill="#8aa4c4" />
    <circle cx="40" cy="62" r="20" fill="#f1c9a5" />
    <circle cx="33" cy="58" r="2.2" fill="#333" />
    <circle cx="47" cy="58" r="2.2" fill="#333" />
    <path d="M32 70q8 6 16 0" stroke="#333" strokeWidth="2" fill="none" strokeLinecap="round" />
  </g>
);

const Label = () => (
  <g transform="translate(168 84) rotate(-6)">
    <rect width="56" height="34" rx="3" fill="#ffffff" stroke="#c9ced6" />
    <rect x="6" y="7" width="40" height="3" rx="1.5" fill="#9aa1ab" />
    <rect x="6" y="14" width="32" height="3" rx="1.5" fill="#9aa1ab" />
    <rect x="6" y="21" width="36" height="3" rx="1.5" fill="#9aa1ab" />
  </g>
);

const SLIDES = [
  {
    title: "Zeige das ganze Produkt",
    text: "Wähle einen Ausschnitt, auf dem das Produkt komplett zu sehen ist, am besten im Querformat.",
    good: (
      <Scene>
        <Mug x={-7} />
      </Scene>
    ),
    bad: (
      <Scene>
        <Mug x={-20} y={-10} scale={1.9} />
      </Scene>
    ),
  },
  {
    title: "Sorge für gutes Licht",
    text: "Fotografiere bei Tageslicht. So sind Farben und Details gut zu erkennen.",
    good: (
      <Scene>
        <Mug x={-7} />
      </Scene>
    ),
    bad: (
      <Scene dim={0.62}>
        <Mug x={-7} />
      </Scene>
    ),
  },
  {
    title: "Achte auf Schärfe",
    text: "Warte kurz, bis die Kamera scharfgestellt hat. Unscharfe Bilder helfen anderen Kunden nicht weiter.",
    good: (
      <Scene>
        <Mug x={-7} />
      </Scene>
    ),
    bad: (
      <Scene blur>
        <Mug x={-7} />
      </Scene>
    ),
  },
  {
    title: "Geh nah genug heran",
    text: "Das Produkt sollte einen großen Teil des Bildes füllen. Ein zweites Foto mit Details ist eine gute Ergänzung.",
    good: (
      <Scene>
        <Mug x={-24} y={-14} scale={1.25} />
      </Scene>
    ),
    bad: (
      <Scene>
        <Mug x={140} y={71} scale={0.4} />
      </Scene>
    ),
  },
  {
    title: "Keine Personen, keine persönlichen Daten",
    text: "Zeige nur das Produkt. Gesichter, Adressen, Rechnungen oder Paketaufkleber gehören nicht ins Bild. Solche Bilder können wir ablehnen.",
    good: (
      <Scene>
        <Mug x={-7} />
      </Scene>
    ),
    bad: (
      <Scene>
        <Person />
        <Mug x={6} y={17} scale={0.85} />
        <Label />
      </Scene>
    ),
  },
  {
    title: "Mach eigene Fotos",
    text: "Lade Fotos von deinem gekauften Produkt hoch, keine Katalog- oder Werbebilder.",
    good: (
      <Scene bg="#efe6d8" table="#b98b5e">
        <path d="M0 135H240M0 148H240" stroke="#a97c50" strokeWidth="1" />
        <Mug x={-7} />
        <path d="M93 98l14-6" stroke="#8fa5c4" strokeWidth="1.6" strokeLinecap="round" />
      </Scene>
    ),
    bad: (
      <Scene bg="#ffffff" table={null}>
        <Mug x={-7} />
        <circle cx="196" cy="34" r="19" fill="#d32f2f" />
        <text x="196" y="39" textAnchor="middle" fontSize="13" fontWeight="700" fill="#ffffff">
          -20%
        </text>
      </Scene>
    ),
  },
];

/* ---------- Bausteine des Fensters ---------- */

const Badge = ({ good }) => (
  <span
    aria-hidden="true"
    style={{
      position: "absolute",
      right: 8,
      bottom: 8,
      width: 28,
      height: 28,
      borderRadius: "50%",
      background: good ? "#2e7d32" : "#c62828",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    }}
  >
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
      <path
        d={good ? "M2 7.5l3.2 3.2L12 3.5" : "M3 3l8 8M11 3l-8 8"}
        stroke="#ffffff"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </span>
);

const Example = ({ good, children }) => (
  <div
    role="group"
    aria-label={good ? "Gutes Beispiel" : "Schlechtes Beispiel"}
    style={{ position: "relative", flex: 1, minWidth: 0 }}
  >
    {children}
    <Badge good={good} />
  </div>
);

const buttonBase = {
  fontFamily: "inherit",
  fontSize: 14,
  fontWeight: 600,
  padding: "10px 22px",
  borderRadius: 999,
  cursor: "pointer",
  border: `1.5px solid ${ACCENT}`,
};

const ReviewPhotoTips = ({ onClose }) => {
  const [index, setIndex] = useState(0);
  const overlayRef = useRef(null);

  const slide = SLIDES[index];
  const isFirst = index === 0;
  const isLast = index === SLIDES.length - 1;

  useEffect(() => {
    overlayRef.current?.focus();
  }, []);

  const next = () => setIndex((value) => Math.min(value + 1, SLIDES.length - 1));
  const prev = () => setIndex((value) => Math.max(value - 1, 0));

  const handleKeyDown = (e) => {
    if (e.key === "Escape") {
      e.stopPropagation();
      onClose();
    } else if (e.key === "ArrowRight") {
      next();
    } else if (e.key === "ArrowLeft") {
      prev();
    }
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <div
      ref={overlayRef}
      role="dialog"
      aria-modal="true"
      aria-labelledby="rvt-title"
      tabIndex={-1}
      onClick={handleBackdropClick}
      onKeyDown={handleKeyDown}
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 2147483647,
        backgroundColor: "rgba(15, 23, 42, 0.55)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
        boxSizing: "border-box",
        outline: "none",
        overscrollBehavior: "contain",
      }}
    >
      <div
        style={{
          backgroundColor: "#ffffff",
          width: "100%",
          maxWidth: 560,
          maxHeight: "92vh",
          overflowY: "auto",
          borderRadius: 16,
          padding: "26px 28px 24px",
          boxSizing: "border-box",
          boxShadow: "0 20px 50px rgba(15, 23, 42, 0.25)",
          color: "#1e293b",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 22 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={ACCENT} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <rect x="3" y="5" width="18" height="14" rx="2" />
              <circle cx="9" cy="10.5" r="1.6" />
              <path d="M21 16l-5-5-9 8" />
            </svg>
            <h2 id="rvt-title" style={{ margin: 0, fontSize: 19, fontWeight: 700, color: "#1e293b" }}>
              {slide.title}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Schließen"
            style={{ background: "none", border: "none", cursor: "pointer", padding: 8, borderRadius: 8, display: "flex", flexShrink: 0 }}
          >
            <svg width="18" height="18" viewBox="0 0 14 14" fill="none" aria-hidden="true">
              <path d="M1 1l12 12M13 1L1 13" stroke={ACCENT} strokeWidth="1.8" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        <div style={{ display: "flex", gap: 12 }}>
          <Example good>{slide.good}</Example>
          <Example good={false}>{slide.bad}</Example>
        </div>

        <p style={{ margin: "16px 0 0", fontSize: 14, lineHeight: 1.5, minHeight: 63, color: "#334155" }} aria-live="polite">
          {slide.text}
        </p>

        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 8, margin: "14px 0 22px" }}>
          {SLIDES.map((item, i) => (
            <button
              key={item.title}
              type="button"
              onClick={() => setIndex(i)}
              aria-label={`Tipp ${i + 1} von ${SLIDES.length}`}
              aria-current={i === index}
              style={{
                width: i === index ? 36 : 8,
                height: 8,
                padding: 0,
                border: "none",
                borderRadius: 4,
                background: i === index ? ACCENT : "#b4bac3",
                cursor: "pointer",
                transition: "width 0.15s ease, background-color 0.15s ease",
              }}
            />
          ))}
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
          <button
            type="button"
            onClick={isFirst ? onClose : prev}
            style={{ ...buttonBase, background: "#ffffff", color: ACCENT }}
          >
            {isFirst ? "Schließen" : "Zurück"}
          </button>
          <button
            type="button"
            onClick={isLast ? onClose : next}
            style={{ ...buttonBase, background: ACCENT, color: "#ffffff" }}
          >
            {isLast ? "Schließen" : "Weiter"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReviewPhotoTips;